/**
 * Points-only Stableford scoring. This module deliberately has no dependency
 * on the money-game models so it is safe to use in the core scorecard.
 */

export type StablefordScoringBasis = 'gross' | 'net';

export const STABLEFORD_POINT_CATEGORIES = [
  'albatrossOrBetter',
  'eagle',
  'birdie',
  'par',
  'bogey',
  'doubleBogeyOrWorse',
] as const;

export type StablefordPointCategory = typeof STABLEFORD_POINT_CATEGORIES[number];

export type StablefordTeamId = string;

export interface StablefordTeam {
  teamId: StablefordTeamId;
  name: string;
  playerIds: string[];
}

/**
 * A multi-team format where every member's Stableford points count. The
 * competition roster is deliberately separate from the point table so an
 * event can have three or more teams without changing its scoring rules.
 */
export interface StablefordTeamCompetition {
  scoringMethod: 'aggregate';
  teams: StablefordTeam[];
}

/** Team-Cup rules layered on top of a Stableford point table. */
export interface StablefordTeamRules {
  /**
   * Each player's target is par-points-per-hole × holes played, less their
   * prorated playing handicap. The result is their handicap-adjusted score.
   */
  targetMode: 'course_handicap';
  /** Limits how far one player can move their team's aggregate score. */
  playerContributionLimit: number;
}

export interface StablefordPointValues {
  albatrossOrBetter: number;
  eagle: number;
  birdie: number;
  par: number;
  bogey: number;
  doubleBogeyOrWorse: number;
}

export interface StablefordConfig {
  scoringBasis: StablefordScoringBasis;
  points: StablefordPointValues;
  /** Optional Team-Cup target and contribution rules. */
  teamRules?: StablefordTeamRules;
  /** Present only when this round sums every player's points by team. */
  teamCompetition?: StablefordTeamCompetition;
}

export interface StablefordHole {
  holeNumber: number;
  par: number;
  strokeIndex: number;
}

export interface StablefordPlayer {
  playerId: string;
  courseHandicap: number;
}

export interface StablefordSavedScore {
  playerId: string;
  gross: number;
}

export interface StablefordHoleScores {
  holeNumber: number;
  scores: readonly StablefordSavedScore[];
}

export interface StablefordPlayerStanding {
  playerId: string;
  points: number;
  holesPlayed: number;
  /** Present for Team-Cup target scoring. */
  targetPoints?: number;
  /** Points relative to that player's handicap target, before any team cap. */
  netPoints?: number;
  /** Contribution included in the team aggregate, after the player cap. */
  teamContribution?: number;
}

export interface StablefordTeamStanding {
  teamId: StablefordTeamId;
  name: string;
  /** Sum of capped player contributions when Team-Cup rules apply. */
  points: number;
  grossPoints?: number;
  netPoints?: number;
  /** Sum of completed player-hole entries for this team. */
  holesPlayed: number;
  playerIds: string[];
}

export interface StablefordStandings {
  standings: StablefordPlayerStanding[];
  pointsByPlayer: Readonly<Record<string, number>>;
  holePointsByPlayer: Readonly<Record<string, Readonly<Record<number, number>>>>;
  teamStandings?: StablefordTeamStanding[];
}

export const DEFAULT_STABLEFORD_POINT_VALUES: Readonly<StablefordPointValues> = Object.freeze({
  albatrossOrBetter: 5,
  eagle: 4,
  birdie: 3,
  par: 2,
  bogey: 1,
  doubleBogeyOrWorse: 0,
});

const MIN_POINT_VALUE = -99;
const MAX_POINT_VALUE = 99;
const MAX_TEAM_NAME_LENGTH = 32;
const MAX_TEAM_ID_LENGTH = 128;
const MAX_TEAM_CONTRIBUTION_LIMIT = 99;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function validPointValue(value: unknown): value is number {
  return typeof value === 'number'
    && Number.isInteger(value)
    && value >= MIN_POINT_VALUE
    && value <= MAX_POINT_VALUE;
}

function parseTeamName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const name = value.trim();
  return name.length > 0 && name.length <= MAX_TEAM_NAME_LENGTH ? name : null;
}

function parseStablefordTeams(value: unknown): StablefordTeam[] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const teamIds = new Set<string>();
  const memberIds = new Set<string>();
  const names = new Set<string>();
  const teams = value.map((candidate) => {
    if (!isRecord(candidate) || typeof candidate.teamId !== 'string') return null;
    const teamId = candidate.teamId.trim();
    const name = parseTeamName(candidate.name);
    const ids = candidate.playerIds;
    if (!teamId || teamId.length > MAX_TEAM_ID_LENGTH || !name || !Array.isArray(ids) || ids.length === 0) return null;
    if (teamIds.has(teamId) || names.has(name.toLocaleLowerCase('en-US'))) return null;
    const normalizedIds = ids.map((id) => typeof id === 'string' ? id.trim() : '');
    if (normalizedIds.some((id) => !id || id.length > 128 || memberIds.has(id))) return null;
    teamIds.add(teamId);
    names.add(name.toLocaleLowerCase('en-US'));
    normalizedIds.forEach((id) => memberIds.add(id));
    return { teamId, name, playerIds: normalizedIds };
  });
  if (teams.some((team) => team == null)) return null;
  return teams as StablefordTeam[];
}

function parseStablefordTeamCompetition(value: unknown): StablefordTeamCompetition | null {
  if (!isRecord(value) || value.scoringMethod !== 'aggregate') return null;
  const teams = parseStablefordTeams(value.teams);
  if (!teams) return null;
  return {
    scoringMethod: 'aggregate',
    teams,
  };
}

function parseStablefordTeamRules(value: unknown): StablefordTeamRules | null {
  if (!isRecord(value) || value.targetMode !== 'course_handicap') return null;
  const playerContributionLimit = value.playerContributionLimit;
  if (!validPointValue(playerContributionLimit)
    || playerContributionLimit < 0
    || playerContributionLimit > MAX_TEAM_CONTRIBUTION_LIMIT) {
    return null;
  }
  return { targetMode: 'course_handicap', playerContributionLimit };
}

function copyPointValues(points: StablefordPointValues): StablefordPointValues {
  return {
    albatrossOrBetter: points.albatrossOrBetter,
    eagle: points.eagle,
    birdie: points.birdie,
    par: points.par,
    bogey: points.bogey,
    doubleBogeyOrWorse: points.doubleBogeyOrWorse,
  };
}

/** A new mutable copy of the standard fixed-target Stableford table. */
export function standardStablefordConfig(): StablefordConfig {
  return {
    scoringBasis: 'gross',
    points: copyPointValues(DEFAULT_STABLEFORD_POINT_VALUES),
  };
}

/** Tyler's Team-Cup defaults. Captains can edit every point value per round. */
export function teamCupStablefordConfig(): StablefordConfig {
  return {
    scoringBasis: 'gross',
    points: {
      albatrossOrBetter: 12,
      eagle: 8,
      birdie: 4,
      par: 2,
      bogey: 1,
      doubleBogeyOrWorse: 0,
    },
    teamRules: {
      targetMode: 'course_handicap',
      playerContributionLimit: 4,
    },
  };
}

/**
 * Returns a canonical config or null. Keeping the range bounded prevents
 * malformed saved payloads from turning a scorecard into an unusable screen.
 */
export function parseStablefordConfig(value: unknown): StablefordConfig | null {
  if (!isRecord(value) || (value.scoringBasis !== 'gross' && value.scoringBasis !== 'net')) {
    return null;
  }
  if (!isRecord(value.points)) return null;
  const candidate = value.points;
  if (!STABLEFORD_POINT_CATEGORIES.every((key) => validPointValue(candidate[key]))) {
    return null;
  }
  const teamCompetition = value.teamCompetition === undefined
    ? undefined
    : parseStablefordTeamCompetition(value.teamCompetition);
  if (value.teamCompetition !== undefined && !teamCompetition) return null;
  const teamRules = value.teamRules === undefined
    ? undefined
    : parseStablefordTeamRules(value.teamRules);
  if (value.teamRules !== undefined && !teamRules) return null;

  return {
    scoringBasis: value.scoringBasis,
    points: {
      albatrossOrBetter: candidate.albatrossOrBetter as number,
      eagle: candidate.eagle as number,
      birdie: candidate.birdie as number,
      par: candidate.par as number,
      bogey: candidate.bogey as number,
      doubleBogeyOrWorse: candidate.doubleBogeyOrWorse as number,
    },
    ...(teamRules ? { teamRules } : {}),
    ...(teamCompetition ? { teamCompetition } : {}),
  };
}

export function isStablefordConfig(value: unknown): value is StablefordConfig {
  return parseStablefordConfig(value) !== null;
}

export function stablefordCategory(relativeToPar: number): StablefordPointCategory {
  if (relativeToPar <= -3) return 'albatrossOrBetter';
  if (relativeToPar === -2) return 'eagle';
  if (relativeToPar === -1) return 'birdie';
  if (relativeToPar === 0) return 'par';
  if (relativeToPar === 1) return 'bogey';
  return 'doubleBogeyOrWorse';
}

export function stablefordPointsForRelativeScore(
  relativeToPar: number,
  config: StablefordConfig,
): number {
  return config.points[stablefordCategory(relativeToPar)];
}

/**
 * Allocates both regular and plus handicaps over the supplied stroke-indexed
 * holes. A positive number is a stroke received. A negative number is a
 * stroke given back.
 */
export function stablefordStrokeAllocation(
  courseHandicap: number,
  holes: readonly StablefordHole[],
): Readonly<Record<number, number>> {
  const allocation: Record<number, number> = {};
  const orderedHoles = holes
    .filter((hole) => Number.isInteger(hole.holeNumber) && Number.isInteger(hole.strokeIndex))
    .slice()
    .sort((left, right) => (
      left.strokeIndex - right.strokeIndex || left.holeNumber - right.holeNumber
    ));
  if (orderedHoles.length === 0 || !Number.isFinite(courseHandicap)) return allocation;

  const sign = Math.sign(courseHandicap);
  let remaining = Math.abs(Math.trunc(courseHandicap));
  while (remaining > 0) {
    for (const hole of orderedHoles) {
      if (remaining === 0) break;
      allocation[hole.holeNumber] = (allocation[hole.holeNumber] ?? 0) + sign;
      remaining -= 1;
    }
  }
  return allocation;
}

export function computeStablefordStandings({
  config,
  holes,
  players,
  holeScores,
}: {
  config: StablefordConfig;
  holes: readonly StablefordHole[];
  players: readonly StablefordPlayer[];
  holeScores: readonly StablefordHoleScores[];
}): StablefordStandings {
  const playerIds = new Set(players.map((player) => player.playerId));
  const holesByNumber = new Map(holes.map((hole) => [hole.holeNumber, hole]));
  const pointsByPlayer: Record<string, number> = {};
  const holesPlayedByPlayer: Record<string, number> = {};
  const holePointsByPlayer: Record<string, Record<number, number>> = {};
  const strokesByPlayer: Record<string, Readonly<Record<number, number>>> = {};

  for (const player of players) {
    pointsByPlayer[player.playerId] = 0;
    holesPlayedByPlayer[player.playerId] = 0;
    holePointsByPlayer[player.playerId] = {};
    strokesByPlayer[player.playerId] = config.scoringBasis === 'net'
      ? stablefordStrokeAllocation(player.courseHandicap, holes)
      : {};
  }

  for (const row of holeScores) {
    const hole = holesByNumber.get(row.holeNumber);
    if (!hole || !Number.isFinite(hole.par)) continue;
    for (const score of row.scores) {
      if (!playerIds.has(score.playerId) || !Number.isFinite(score.gross)) continue;
      const netAdjustment = strokesByPlayer[score.playerId][row.holeNumber] ?? 0;
      const relativeToPar = score.gross - netAdjustment - hole.par;
      const points = stablefordPointsForRelativeScore(relativeToPar, config);
      pointsByPlayer[score.playerId] += points;
      holesPlayedByPlayer[score.playerId] += 1;
      holePointsByPlayer[score.playerId][row.holeNumber] = points;
    }
  }

  const targetByPlayer: Record<string, number> = {};
  const netPointsByPlayer: Record<string, number> = {};
  const teamContributionByPlayer: Record<string, number> = {};
  if (config.teamRules?.targetMode === 'course_handicap') {
    const parPointTarget = config.points.par;
    for (const player of players) {
      const holesPlayed = holesPlayedByPlayer[player.playerId];
      const target = (parPointTarget * holesPlayed)
        - (player.courseHandicap * holesPlayed / Math.max(1, holes.length));
      const netPoints = pointsByPlayer[player.playerId] - target;
      targetByPlayer[player.playerId] = target;
      netPointsByPlayer[player.playerId] = netPoints;
      teamContributionByPlayer[player.playerId] = Math.max(
        -config.teamRules.playerContributionLimit,
        Math.min(config.teamRules.playerContributionLimit, netPoints),
      );
    }
  }

  const teamStandings = config.teamCompetition?.teams.map((team) => {
    const grossPoints = team.playerIds.reduce(
      (total, playerId) => total + (pointsByPlayer[playerId] ?? 0),
      0,
    );
    const netPoints = team.playerIds.reduce(
      (total, playerId) => total + (netPointsByPlayer[playerId] ?? 0),
      0,
    );
    return {
      teamId: team.teamId,
      name: team.name,
      points: config.teamRules
        ? team.playerIds.reduce(
          (total, playerId) => total + (teamContributionByPlayer[playerId] ?? 0),
          0,
        )
        : grossPoints,
      ...(config.teamRules ? { grossPoints, netPoints } : {}),
      holesPlayed: team.playerIds.reduce(
        (total, playerId) => total + (holesPlayedByPlayer[playerId] ?? 0),
        0,
      ),
      playerIds: [...team.playerIds],
    };
  });

  return {
    standings: players.map((player) => ({
      playerId: player.playerId,
      points: pointsByPlayer[player.playerId],
      holesPlayed: holesPlayedByPlayer[player.playerId],
      ...(config.teamRules ? {
        targetPoints: targetByPlayer[player.playerId],
        netPoints: netPointsByPlayer[player.playerId],
        teamContribution: teamContributionByPlayer[player.playerId],
      } : {}),
    })),
    pointsByPlayer,
    holePointsByPlayer,
    ...(teamStandings ? { teamStandings } : {}),
  };
}
